import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Box, Alert, CircularProgress, Typography, Grid } from "@mui/material";

import { FinanceCompareBarChart, FinancePieChart, HeaderPage } from "../../components/Admin";


import { FinanceAdsComparisonTable, FinanceDailySalesChart, FinanceFiscalCards, FinanceKpiCards, FinanceRecurringCards, FinanceSalesTable, FinanceSecondaryKpis, FinanceServiceMixChart, FinanceToolbar, FinanceViewToggle } from "../../components/Admin/Finance/Dashboard";

import { toYmd } from "../../utils/finance/format";
import { loadSavedRange, saveRange } from "../../utils/finance/rangeStorage";
import { useMetrics } from "../../hook/useMetrics";


export function FinanceDashboard() {
  const { loading, error, financeDashboard, getFinanceDashboard } = useMetrics();

  // ===============================
  // Estado inicial (con persistencia)
  // ===============================
  const saved = loadSavedRange();
  const [viewMode, setViewMode] = useState("commercial");
  const [preset, setPreset] = useState(saved?.preset || "this_month");

  const [monthDate, setMonthDate] = useState(() => {
    if (saved?.month) return dayjs(saved.month + "-01").toDate();
    return dayjs().startOf("month").toDate();
  });

  const [startDate, setStartDate] = useState(() => {
    if (saved?.from) return dayjs(saved.from).toDate();
    return dayjs().startOf("month").toDate();
  });

  const [endDate, setEndDate] = useState(() => {
    if (saved?.to) return dayjs(saved.to).toDate();
    return dayjs().toDate();
  });

  const adsCurrent = financeDashboard?.ads_summary;
const adsCompare = financeDashboard?.ads_summary_compare;

const adsChartRows =
  adsCurrent && adsCompare
    ? [
        { label: "Ventas Ads", a: adsCurrent.sales_pen, b: adsCompare.sales_pen },
        { label: "Gasto Ads", a: adsCurrent.expenses_pen, b: adsCompare.expenses_pen },
        { label: "Neto", a: adsCurrent.net_pen, b: adsCompare.net_pen },
      ]
    : [];


  // ===============================
  // Presets (tabs)
  // ===============================
  const applyPreset = (nextPreset, baseMonth = monthDate) => {
    const m = dayjs(baseMonth);

    if (nextPreset === "this_month") {
      setStartDate(m.startOf("month").toDate());
      setEndDate(m.endOf("month").toDate());
    }

    if (nextPreset === "last_month") {
      const prev = m.subtract(1, "month");
      setStartDate(prev.startOf("month").toDate());
      setEndDate(prev.endOf("month").toDate());
      setMonthDate(prev.startOf("month").toDate());
    }

    if (nextPreset === "last_6_months") {
      setStartDate(dayjs().subtract(5, "month").startOf("month").toDate());
      setEndDate(dayjs().endOf("month").toDate());
    }

    if (nextPreset === "last_year") {
      setStartDate(dayjs().subtract(11, "month").startOf("month").toDate());
      setEndDate(dayjs().endOf("month").toDate());
    }

    setPreset(nextPreset);
  };

  // ===============================
  // Cargar dashboard
  // ===============================
  const params = useMemo(() => ({
    from: toYmd(startDate),
    to: toYmd(endDate),
    compare: "previous_month",
  }), [startDate, endDate]);

  useEffect(() => {
    getFinanceDashboard(params);
    // eslint-disable-next-line
  }, [params.from, params.to]);

  // ===============================
  // Persistir rango
  // ===============================
  useEffect(() => {
    saveRange({
      preset,
      month: dayjs(monthDate).format("YYYY-MM"),
      from: toYmd(startDate),
      to: toYmd(endDate),
    });
  }, [preset, monthDate, startDate, endDate]);

  // ===============================
  // Render
  // ===============================
  return (
    <>
      <HeaderPage title="Dashboard de Finanzas" />

      <Box p={2}>
        <FinanceToolbar
          monthDate={monthDate}
          onPrevMonth={() => {
            const prev = dayjs(monthDate).subtract(1, "month").startOf("month").toDate();
            setMonthDate(prev);
            applyPreset("this_month", prev);
          }}
          onNextMonth={() => {
            const next = dayjs(monthDate).add(1, "month").startOf("month").toDate();
            setMonthDate(next);
            applyPreset("this_month", next);
          }}
          preset={preset}
          onChangePreset={(v) => applyPreset(v)}
          startDate={startDate}
          endDate={endDate}
          onChangeStart={(d) => {
            setPreset("custom");
            setStartDate(d);
          }}
          onChangeEnd={(d) => {
            setPreset("custom");
            setEndDate(d);
          }}
        />

        <Box mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <FinanceViewToggle value={viewMode} onChange={setViewMode} />
      </Box>

        {error && (
          <Box mt={2}>
            <Alert severity="error">Error cargando dashboard.</Alert>
          </Box>
        )}

        {loading && (
          <Box mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        <Box mt={2}>
        {viewMode === "commercial" ? (
          <FinanceKpiCards kpis={financeDashboard?.kpis} />
        ) : (
          <FinanceFiscalCards fiscal={financeDashboard?.fiscal} />
        )}
      </Box>

        

        <Box mt={2}>
        <FinanceSecondaryKpis kpis={financeDashboard?.kpis} />
      </Box>


      

      <Box mt={3}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Recurrentes
          </Typography>

          <FinanceRecurringCards recurring={financeDashboard?.recurring} />
        </Box>

        <Box mt={4}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FinancePieChart
            title="Ventas por tipo de servicio"
            data={financeDashboard?.breakdowns?.sales_by_service_type}
            labelKey="service_type"
            valueKey="total"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FinancePieChart
            title="Ventas por canal de adquisición"
            data={financeDashboard?.breakdowns?.sales_by_acquisition_channel}
            labelKey="acquisition_channel"
            valueKey="total"
          />
        </Grid>

 

        <Grid item xs={12} md={4}>
          <FinancePieChart
            title="Gastos por categoría"
            data={financeDashboard?.breakdowns?.expenses_by_category}
            labelKey="category"
            valueKey="total"
          />
        </Grid>



      </Grid>
    </Box>


    <Box mt={3}>
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <FinanceCompareBarChart
        title="Publicidad: Actual vs comparativo"
        data={adsChartRows}
        aLabel="Actual"
        bLabel="Comparativo"
      />
    </Grid>

    <Grid item xs={12} md={6}>
      <FinanceAdsComparisonTable
        current={adsCurrent}
        compare={adsCompare}
      />
    </Grid>
  </Grid>
</Box>



        <Box mt={2}>
          <FinanceDailySalesChart series={financeDashboard?.series} />
        </Box>


        <Box mt={2}>
          <FinanceServiceMixChart breakdowns={financeDashboard?.breakdowns} />
        </Box>

        <Box mt={2}>
          <FinanceSalesTable rows={financeDashboard?.tables?.sales || []} />
        </Box>


      </Box>
    </>
  );
}
